type ScrollTarget = HTMLElement | Window;
export type ScrollAxis = 'x' | 'y';

const FALLBACK_FRAME_MS = 16;

const easeInOutCubic = (progress: number): number => (progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2);

const getScrollPosition = (target: ScrollTarget, axis: ScrollAxis): number => {
	if (target instanceof Window) return axis === 'y' ? target.scrollY : target.scrollX;
	return axis === 'y' ? target.scrollTop : target.scrollLeft;
};

const setScrollPosition = (target: ScrollTarget, axis: ScrollAxis, value: number): void => {
	if (target instanceof Window) {
		if (axis === 'y') {
			target.scrollTo(target.scrollX, value);
		} else {
			target.scrollTo(value, target.scrollY);
		}
		return;
	}
	if (axis === 'y') {
		target.scrollTop = value;
	} else {
		target.scrollLeft = value;
	}
};

export const scheduleFrame = (callback: (now: number) => void): (() => void) => {
	if (document.visibilityState === 'visible') {
		const frame = requestAnimationFrame(callback);
		return () => cancelAnimationFrame(frame);
	}
	const timer = window.setTimeout(() => callback(performance.now()), FALLBACK_FRAME_MS);
	return () => window.clearTimeout(timer);
};

export function animateScrollTo(target: ScrollTarget, destination: number, duration: number, axis: ScrollAxis = 'y'): () => void {
	const start = getScrollPosition(target, axis);
	const distance = destination - start;
	if (distance === 0 || duration <= 0) {
		setScrollPosition(target, axis, destination);
		return () => undefined;
	}

	let cancelFrame: () => void = () => undefined;
	const startedAt = performance.now();

	const step = (now: number): void => {
		const progress = Math.min((now - startedAt) / duration, 1);
		setScrollPosition(target, axis, start + distance * easeInOutCubic(progress));
		if (progress < 1) cancelFrame = scheduleFrame(step);
	};

	cancelFrame = scheduleFrame(step);
	return () => cancelFrame();
}

export function findScrollContainer(element: HTMLElement): ScrollTarget {
	let container: HTMLElement | null = element.parentElement;
	while (container && container !== document.body) {
		const { overflowY } = getComputedStyle(container);
		if ((overflowY === 'auto' || overflowY === 'scroll') && container.scrollHeight > container.clientHeight) return container;
		container = container.parentElement;
	}
	return window;
}
